import React from "react";
import { ResponsiveContainer, CartesianGrid, Bar, BarChart as RechartsBarChart, YAxis, XAxis, Label as RechartsLabel, Tooltip, ReferenceLine, Brush, Rectangle } from 'recharts';
import { scaleLinear as d3ScaleLinear } from 'd3-scale';
import { axisFormat, failedColor, firedColor, strokeColor, tickFormat, tickMargin } from "./styles";
import { find, max, maxBy } from "lodash";
import { Flex } from "../Flex";
import { Label } from "../Text";
import { formatDate } from "../../utils";
import { useState } from "react";
import { useEffect } from "react";
import { ChartTooltip } from "../Tooltip";
import { LoadingBarChart } from "./LoadingBarChart";

const CustomTick = (props) => {
    let { x, y, index, visibleTicksCount, payload } = props,
    isLastTick = (index === visibleTicksCount - 1),
    yOffset = 8,
    lineStyle = {};
    y = payload.coordinate;

    lineStyle = index === 0 || isLastTick ? axisFormat : { stroke:"#ccc", strokeDasharray: "3 3" };
    return (
        <g>
            <line  {...lineStyle} fill="none" x1={0} x2={x + tickMargin} y1={isLastTick ? tickMargin : y} y2={isLastTick ? tickMargin : y} />
            <text x={x} y={isLastTick ? y : y - yOffset} style={tickFormat} textAnchor="end" >
                <tspan x={x} dy="0.355em" >{payload.value}</tspan>
            </text>
        </g>
      );
};

const generateData = (data=[], start, stop) => {
    let newdata = [];
    let day = start.clone();
    while (day <= stop) {
        let strDay = formatDate(day, "DD-MMM-YYYY"), item = find(data, d => formatDate(d.date, "DD-MMM-YYYY") == strDay);
        if (item) {
            const { fired, failed, date, node, key } = item;
            let dispFailed = failed/fired > 0 && failed/fired < 0.1 ? Math.ceil(fired * 0.1) : failed;
            newdata.push({
                key: key,
                date: date,
                node: node,
                fired: fired,
                failed: failed,
                dispFired: fired - dispFailed,
                dispFailed: dispFailed
            })
        } else {
            // if there's no data for a day then fill it with null for proper rendering
            // let fakeFired = Math.random()*1000, fakeFailed = Math.floor(Math.random()*100);
            newdata.push({
                date: strDay,
                // fired: fakeFired,
                // failed: fakeFailed,
                // dispFailed: fakeFailed,
                // dispFired: fakeFired - fakeFailed
            });
        };
        day = day.add(1, 'day').endOf('day');
    };

    return newdata;
};


const BarChart = ({
    node,
    data=[],
    dashboard,
    dateRange=[],
    onlyFailed,
    loading,
    onBarClick
}) => {
    const initialState = {
        data: [],
        startIndex: 0,
        endIndex: 30
    };
    const [ state, setState ] = useState(initialState);


    useEffect(() => {
        if (!loading) {
            const fullData = generateData(data, dateRange[0], dateRange[1]);
            setState({
                ...state,
                data: fullData,
                startIndex: 0,
                endIndex: fullData.length - 1,
            })
        }
    }, [ loading, dateRange[0], dateRange[1] ]);

    if (loading) {
        return <LoadingBarChart />
    }

    const yDomain = data.length
        ? onlyFailed ? [0, maxBy(data, "failed").failed]  : [0, maxBy(data, "fired").fired]
        : [0, 100],
    yTicks = d3ScaleLinear().domain(yDomain).nice(1).ticks(5);

    const getTickWidth = (tick) => {
        let width = 20, tickWidth = 0;
        let canvas = document.createElement("canvas"),
        context = canvas.getContext("2d");
        context.font = "500 10px Arial";
        tickWidth = Math.ceil(context.measureText(tick).width);
        if (tickWidth > width) width = tickWidth;

        return width + tickMargin;
    };

    const handleBarClick = (data, index) => {
        if (data && data.node && data.key) onBarClick(data.node, data.key);
    }

    const renderChartContent = () => {
        let chartContent = onlyFailed
            ? <Bar
                key="bar-0"
                dataKey="failed"
                radius={[4, 4, 0, 0,]}
                isAnimationActive={false}
                fill={failedColor}
                activeBar={<Rectangle fill="#CC3131" />}
                onClick={handleBarClick}
            />
            : [
                <Bar
                    key="bar-0"
                    dataKey="dispFailed"
                    stackId="a"
                    isAnimationActive={false}
                    fill={failedColor}
                    activeBar={<Rectangle fill="#CC3131" />}
                    onClick={handleBarClick}
                />,
                <Bar
                    key="bar-1"
                    dataKey="dispFired"
                    stackId="a"
                    isAnimationActive={false}
                    radius={[4, 4, 0, 0,]}
                    fill={firedColor}
                    activeBar={<Rectangle cursor="pointer" fill="#4B78C3"/>}
                    onClick={handleBarClick}
                />
            ];

        return chartContent;
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload[0] && !(onlyFailed && payload[0].payload.failed == 0)) {
            return (
                <ChartTooltip>
                    <Label bold size="14px" color="#fff">{node}</Label>
                    <Flex gap={{ right: 8 }}>
                        <Label capitalize color="#fff">Fired:</Label>
                        <Label capitalize color="#fff">{payload[0].payload.fired}</Label>
                    </Flex>
                    <Flex gap={{ right: 8 }}>
                    <Label capitalize color="#fff">Failed:</Label>
                        <Label capitalize color="#fff">{payload[0].payload.failed}</Label>
                    </Flex>
                </ChartTooltip>
            );
        }

        return null;
    };


    const onBrushChange = (e) => {
        setState(state => ({ ...state, startIndex: e.startIndex, endIndex: e.endIndex }));
    };

    const onChartWheel = (e) => {
        if (!loading && !dashboard) {
            const { data, startIndex, endIndex } = state;
            // let newdata = JSON.parse(JSON.stringify(data)),
            let start = startIndex, end = endIndex, max = data.length - 1;
            let delta = e.deltaY || e.detail || e.wheelDelta;
            if (delta < 0) {
                // zoom in
                let zoom = (end - start)/10;
                if (zoom >= 1) {
                    start += zoom;
                    end -= zoom;
                };
            } else {
                // zoom out
                start = start - (end - start)/2; if (start < 0) start = 0;
                end = end + (end - start)/2; if (end > max) end = max;
            }
            start = Math.floor(start);
            end = Math.floor(end);
            if (start != startIndex || end != endIndex) {
                setState(state => ({
                    ...state,
                    // data: newdata,
                    startIndex: start,
                    endIndex: end
                }));
            }
        }
    };

    return(
        <div style={{ width: "100%", height: "calc(100% - 14px)"}} onWheel={onChartWheel}>
            <ResponsiveContainer>
                <RechartsBarChart data={state.data}  margin={{ top: tickMargin }}>
                    <CartesianGrid key="cartesian-grid" stroke="#ccc" strokeDasharray="3 3" />
                    <Tooltip
                        labelStyle={{ display: "none" }}
                        content={<CustomTooltip />}
                        cursor={{ fill: "transparent" }}
                    />
                    <XAxis
                        key="x-axis-top"
                        mirror
                        xAxisId={1}
                        orientation="top"
                        tick={false}
                        axisLine={axisFormat}
                    />
                    <XAxis
                        key="x-axis-bottom"
                        xAxisId={0}
                        dataKey="date"
                        tickSize={0}
                        tickMargin={tickMargin}
                        axisLine={axisFormat}
                        height={dashboard ? 6 : 40}
                        interval="preserveStartEnd"
                        tick={dashboard ? false : tickFormat}
                        tickFormatter={tick => formatDate(tick, "DD MMM YYYY")}
                    >
                            {!dashboard && <RechartsLabel style={{ fontSize: "10px", fontWeight: 600, textTransform: "uppercase", fill: "#8897af" }} offset={0} dy={-5} position="insideBottomLeft" value="begin time" />}
                            {!dashboard && <RechartsLabel style={{ fontSize: "10px", fontWeight: 600, textTransform: "uppercase", fill: "#8897af" }} value="end time" offset={0} dy={-5} position="insideBottomRight" />}
                    </XAxis>
                    <YAxis
                        key="y-axis-0"
                        tickSize={0}
                        tickMargin={tickMargin}
                        ticks={yTicks}
                        axisLine={false}
                        domain={yDomain}
                        hide={!data.length}
                        tick={<CustomTick />}
                        interval="preserveStartEnd"
                        padding={{ top: !data.length ? 0 : 8 }}
                        width={max(yTicks.map(tick => getTickWidth(tick)))}
                    />
                    {data.length
                        ? null
                        : <ReferenceLine stroke={"transparent"} y={50}>
                        <RechartsLabel style={{ fontSize: "18px", fontWeight: 600, textTransform: "uppercase", fill: "#8897af" }}>no data</RechartsLabel>
                    </ReferenceLine>}
                    {renderChartContent()}
                    {!dashboard &&
                        <Brush
                            height={68}
                            dataKey="date"
                            stroke={strokeColor}
                            startIndex={state.startIndex}
                            endIndex={state.endIndex}
                            onChange={onBrushChange}
                            tickFormatter={tick => null}
                        >
                            <RechartsBarChart>
                                <XAxis
                                    hide={true}
                                    dataKey="date"
                                    key="x-axis-brush"
                                />
                                <YAxis
                                    key="y-axis-brush"
                                    hide={true}
                                    domain={yDomain}
                                    padding={{ top: !data.length ? 0 : 8 }}
                                />
                                {renderChartContent()}
                            </RechartsBarChart>
                        </Brush>}
                </RechartsBarChart>
            </ResponsiveContainer>
            <Flex justify="space-between">
                <Label style={{ paddingLeft: !data.length || dashboard ? 0 : max(yTicks.map(tick => getTickWidth(tick))) }} bold capitalize size="10px">{formatDate(dateRange[0], "DD MMM YYYY")}</Label>
                <Label bold capitalize size="10px">{formatDate(dateRange[1], "DD MMM YYYY")}</Label>
            </Flex>
        </div>
    )
}

export { BarChart }